import React, { useState, useRef, useCallback } from 'react';
import { Check, Edit2, CircleMinus, CircleCheck } from 'lucide-react';
import { Agency, ProcedureTemplate, ProcedureTemplateCreatePayload } from '../../services/auditService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import ProcedureModal from './ProcedureModal';
import ProcedureChecklist from './procedure-types/ProcedureChecklist';
import ProcedureQuestionnaire from './procedure-types/ProcedureQuestionnaire';
import ProcedureRating from './procedure-types/ProcedureRating';
import ProcedureForm from './procedure-types/ProcedureForm';
import ProcedureWalkthrough from './procedure-types/ProcedureWalkthrough';
import ProcedureSopWalkthrough from './procedure-types/ProcedureSopWalkthrough';
import ProcedureRiskAssessment, { ProcedureConfigHandle } from './procedure-types/ProcedureRiskAssessment';
import ProcedureControlTest from './procedure-types/ProcedureControlTest';
import ProcedureConfirmations from './procedure-types/ProcedureConfirmations';
import ProcedurePhysicalInspection from './procedure-types/ProcedurePhysicalInspection';

interface ProcedureConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    procedure: ProcedureTemplate | null;
    agencies?: Agency[];
    onSave: (data: ProcedureTemplateCreatePayload, id?: number) => Promise<void> | void;
    onToggleActive?: (id: number, next: 0 | 1) => Promise<void> | void;
}

type Tab = 'Basic Details' | 'Configuration';

const ProcedureConfigModal: React.FC<ProcedureConfigModalProps> = ({
    isOpen, onClose, procedure, agencies = [], onSave, onToggleActive,
}) => {
    const [tab, setTab] = useState<Tab>('Basic Details');
    const [isEditing, setIsEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const editFormRef = useRef<HTMLFormElement>(null);

    // Configuration tab — drive the type-specific editor's save from the modal footer.
    const configRef = useRef<ProcedureConfigHandle>(null);
    const [configSaving, setConfigSaving] = useState(false);
    const [configLoading, setConfigLoading] = useState(false);
    const handleConfigStatus = useCallback((s: { saving: boolean; loading: boolean }) => {
        setConfigSaving(s.saving);
        setConfigLoading(s.loading);
    }, []);

    if (!procedure) return null;

    const handleSave = async (data: ProcedureTemplateCreatePayload, id?: number) => {
        // Approved templates are frozen — mirror the CAT template config: block the
        // save. Reopen via the status dot to edit.
        if (procedure.workflow_state === 'approved') return;
        setSubmitting(true);
        try {
            await onSave(data, id);
            setIsEditing(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async () => {
        if (!procedure || !onToggleActive) return;
        setToggling(true);
        try {
            const isActive = (procedure.is_active ?? 1) === 1;
            await onToggleActive(procedure.id, isActive ? 0 : 1);
            onClose();
        } finally {
            setToggling(false);
        }
    };

    const renderConfigComponent = () => {
        const props = { procedure };
        switch (procedure.procedure_type) {
            case 'Checklist':             return <ProcedureChecklist {...props} />;
            case 'Questionnaire':         return <ProcedureQuestionnaire {...props} />;
            case 'Rating':                return <ProcedureRating {...props} />;
            case 'Form':                  return <ProcedureForm {...props} />;
            case 'Walkthrough':           return <ProcedureWalkthrough {...props} />;
            case 'WalkthroughDesign':     return <ProcedureSopWalkthrough {...props} />;
            case 'WalkthroughCompliance': return <ProcedureSopWalkthrough {...props} />;
            case 'RiskAssessment':        return <ProcedureRiskAssessment {...props} ref={configRef} onStatusChange={handleConfigStatus} />;
            case 'ControlTest':           return <ProcedureControlTest {...props} ref={configRef} onStatusChange={handleConfigStatus} />;
            case 'Confirmation':          return <ProcedureConfirmations {...props} />;
            case 'PhysicalCheck':         return <ProcedurePhysicalInspection {...props} />;
            default:
                return <div className="text-gray-500 italic p-6">No configuration available for this type.</div>;
        }
    };

    const isDetailsTab = tab === 'Basic Details';
    // Config types whose editor exposes a save() handle for the modal footer.
    const configHasFooterSave = procedure.procedure_type === 'RiskAssessment'
        || procedure.procedure_type === 'ControlTest';
    // Approved templates are frozen — mirror the CAT template config: the footer
    // Save button looks locked and the underlying save() surfaces a toast.
    const isApproved = procedure.workflow_state === 'approved';

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnOverlayClick={false}>
            <Modal.Header>
                {isApproved && isDetailsTab ? (
                    <>Viewing (locked) — {procedure.procedure_name}</>
                ) : (
                    <>Procedure Configuration — {procedure.procedure_name}</>
                )}
            </Modal.Header>

            <Modal.Body>
                <div className="app-modal-tabs">
                    {(['Basic Details', 'Configuration'] as Tab[]).map(t => (
                        <button
                            key={t}
                            type="button"
                            data-testid={`TAB_procedure_config_${t.toLowerCase().replace(/\s+/g, '_')}`}
                            onClick={() => setTab(t)}
                            className={`app-modal-tab${tab === t ? ' app-modal-tab--active' : ''}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>

                {isDetailsTab && (
                    <>
                        {isApproved && (
                            <div className="approved-banner" data-testid="BANNER_procedure_config_locked">
                                Record is approved and locked. Reopen via the workflow button to edit.
                            </div>
                        )}
                        <ProcedureModal
                            isOpen={true}
                            embedded
                            onClose={() => {/* handled by parent */}}
                            onSave={handleSave}
                            initialData={procedure}
                            agencies={agencies}
                            disabled={isApproved || !isEditing}
                            hideEmbeddedFooter={true}
                            embeddedFormRef={editFormRef}
                        />
                    </>
                )}

                {tab === 'Configuration' && (
                    <div className="p-0">
                        {renderConfigComponent()}
                    </div>
                )}
            </Modal.Body>

            {isDetailsTab && !isApproved && (
                <Modal.Footer className="app-modal-footer--split">
                    {onToggleActive && (
                        <Button
                            variant={(procedure?.is_active ?? 1) === 1 ? 'inactive-outline' : 'active-outline'}
                            icon={(procedure?.is_active ?? 1) === 1 ? CircleMinus : CircleCheck}
                            type="button"
                            data-testid="BTN_procedure_config_toggle_active"
                            onClick={handleToggleActive}
                            disabled={toggling}
                        >
                            {toggling ? '...' : (procedure?.is_active ?? 1) === 1 ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    <div className="flex gap-2">
                        {isEditing ? (
                            <Button
                                type="button"
                                variant="update"
                                icon={Check}
                                data-testid="BTN_procedure_config_update"
                                disabled={submitting}
                                onClick={() => editFormRef.current?.requestSubmit()}
                            >
                                Update
                            </Button>
                        ) : (
                            <Button
                                variant="fill"
                                icon={Edit2}
                                data-testid="BTN_procedure_config_edit"
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                </Modal.Footer>
            )}

            {!isDetailsTab && configHasFooterSave && (
                <Modal.Footer>
                    <Button
                        type="button"
                        variant="update"
                        icon={Check}
                        data-testid="BTN_procedure_config_save_configuration"
                        disabled={configSaving || configLoading}
                        className={isApproved ? 'opacity-50 cursor-not-allowed' : ''}
                        title={isApproved ? 'Record is approved and locked. Reopen via the workflow button to edit.' : undefined}
                        onClick={() => configRef.current?.save()}
                    >
                        Save Configuration
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default ProcedureConfigModal;
